import React, { useMemo, useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Upload,
  Button,
  Steps,
  Typography,
  Table,
  Alert,
  Space,
  Progress,
  Result,
  Card,
  Row,
  Col,
  Tag,
  message,
  Tooltip,
  Checkbox,
} from 'antd';
import * as XLSX from 'xlsx';
import {
  UploadOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import type { UploadProps, UploadFile } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import apiService from '@/services/apiService';
import { useTranslation } from "react-i18next";
import {
  findBundleSheetName,
  parseBundleSheetRows,
  referencedSkusOf,
  validateBundleSheet,
  type BundleSheetRow,
  type ResolvedSku,
} from './productBundleSheet';

// Customer interface for validation
interface CustomerInfo {
  id: string;
  name: string;
}

const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;

interface ValidationError {
  row: number;
  field: string;
  value: any;
  error: string;
  severity: 'error' | 'warning';
  sheet?: 'products' | 'bundles';
}

interface PreviewItem {
  row: number;
  merchantReference: string;
  sku: string;
  productName: string;
  productNameEnglish?: string;
  productNameChinese?: string;
  description?: string;
  barcode: string;
  brand?: string;
  length?: number;
  width?: number;
  height?: number;
  weight?: number;
  packagingLength?: number;
  packagingWidth?: number;
  packagingHeight?: number;
  packagingWeight?: number;
  temperatureRequirement?: string;
  baseUOM: string;
  packagingUOM?: string;
  conversionFactor?: number;
  currentStock: number;
  reorderLevel: number;
  safetyStock: number;
  maxStock: number;
  customer?: string;
  storeCode?: string;
  batchControlType?: string;
  bundleSummary?: string;
  status: 'valid' | 'error' | 'warning';
  errors: ValidationError[];
}

export interface BundleComponentPayload {
  parentSku: string;
  componentSku: string;
  quantity: number;
}

interface ProductBulkUploadModalProps {
  visible: boolean;
  onCancel: () => void;
  onUploadComplete: (data: any[], bundleComponents: BundleComponentPayload[]) => void;
}

const ProductBulkUploadModal: React.FC<ProductBulkUploadModalProps> = ({
  visible,
  onCancel,
  onUploadComplete,
}) => {
    const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [uploadedFile, setUploadedFile] = useState<UploadFile | null>(null);
  const [previewData, setPreviewData] = useState<PreviewItem[]>([]);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [customers, setCustomers] = useState<CustomerInfo[]>([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [existingSkus, setExistingSkus] = useState<Map<string, string>>(new Map()); // SKU -> Product Name
  const [skusLoading, setSkusLoading] = useState(false);
  const [validBundles, setValidBundles] = useState<Map<string, BundleSheetRow[]>>(new Map());

  // Fetch existing products (SKUs) for duplicate validation
  const fetchExistingSkus = useCallback(async () => {
    setSkusLoading(true);
    try {
      // Fetch all products to get existing SKUs
      const response: any = await apiService.getProducts({ page: 1, limit: 10000 });

      let productList: any[] = [];
      if (response && Array.isArray(response)) {
        productList = response;
      } else if (response?.products && Array.isArray(response.products)) {
        productList = response.products;
      } else if (response?.data && Array.isArray(response.data)) {
        productList = response.data;
      }

      if (productList.length > 0) {
        const skuMap = new Map<string, string>();
        productList.forEach((p: any) => {
          const sku = p.itemCode || p.sku;
          const name = p.itemName || p.productName || p.name || '';
          if (sku) {
            skuMap.set(sku.toLowerCase(), name);
          }
        });
        setExistingSkus(skuMap);
        console.log(`Loaded ${skuMap.size} existing SKUs for duplicate validation`);
      }
    } catch (error) {
      console.error('Failed to fetch existing SKUs:', error);
      message.warning(t('無法載入現有產品列表，重複SKU驗證將被跳過'));
    } finally {
      setSkusLoading(false);
    }
  }, []);

  // Fetch customers for validation
  const fetchCustomers = useCallback(async () => {
    setCustomersLoading(true);
    try {
      // Use getCustomers API with high limit to get all customers
      // Note: Don't filter by companyId or isActive to match ProductMasterSetup behavior
      const response: any = await apiService.getCustomers({ page: 1, limit: 1000 });

      // Handle different response formats
      let customerList: any[] = [];
      if (response && Array.isArray(response)) {
        customerList = response;
      } else if (response?.customers && Array.isArray(response.customers)) {
        customerList = response.customers;
      } else if (response?.data && Array.isArray(response.data)) {
        customerList = response.data;
      }

      if (customerList.length > 0) {
        const mappedCustomers = customerList.map((c: any) => ({
          id: c.id,
          name: c.name || ''
        }));
        setCustomers(mappedCustomers);
        console.log(`Loaded ${customerList.length} customers for validation:`, mappedCustomers.map(c => c.name));
      } else {
        console.warn('No customers found in response:', response);
      }
    } catch (error) {
      console.error('Failed to fetch customers for validation:', error);
      message.warning(t('無法載入客戶列表，客戶驗證將被跳過'));
    } finally {
      setCustomersLoading(false);
    }
  }, []);

  // 重置狀態
  const resetState = () => {
    setCurrentStep(0);
    setUploadedFile(null);
    setPreviewData([]);
    setValidationErrors([]);
    setIsProcessing(false);
    setUploadProgress(0);
    setValidBundles(new Map());
  };

  // 當模態框打開時重置狀態並載入客戶列表和現有SKU
  useEffect(() => {
    if (visible) {
      resetState();
      fetchCustomers();
      fetchExistingSkus();
    }
  }, [visible, fetchCustomers, fetchExistingSkus]);

  // 字段說明
  const fieldDescriptions = [
    // Store Code and Customer are a conditional pair — a product needs at least
    // one of them, so neither can be flagged unconditionally required here.
    { field: 'customer', name: '客戶', required: false, description: `${t('產品所屬客戶（可填寫客戶名稱，如：ABC Company）')} — ${t('Store Code 或客戶至少填寫一個')}` },
    { field: 'storeCode', name: 'Store Code', required: false, description: `${t('產品所屬 Store Code（如：H6620001，多個用逗號分隔）')} — ${t('Store Code 或客戶至少填寫一個')}` },
    { field: 'batchControlType', name: '預設訂單分配策略', required: true, description: t('分配策略：FIFO（先進先出）、FEFO（先到期先出）、LARGEST_QTY_FIRST（最大庫存優先）') },
    { field: 'merchantReference', name: '商家參考', required: false, description: t('商家的產品參考編號（可選）') },
    { field: 'sku', name: 'SKU ID', required: true, description: t('產品的SKU識別碼') },
    { field: 'barcode', name: '條形碼', required: true, description: t('產品條形碼') },
    { field: 'productName', name: '產品名稱', required: true, description: t('產品名稱') },
    { field: 'productNameEnglish', name: '英文產品名稱', required: false, description: t('產品英文名稱') },
    { field: 'productNameChinese', name: '中文產品名稱', required: false, description: t('產品中文名稱') },
    { field: 'description', name: '描述', required: false, description: t('產品描述') },
    { field: 'brand', name: '品牌', required: false, description: t('產品品牌') },
    { field: 'length', name: '產品長度(cm)', required: false, description: t('產品本身長度') },
    { field: 'width', name: '產品寬度(cm)', required: false, description: t('產品本身寬度') },
    { field: 'height', name: '產品高度(cm)', required: false, description: t('產品本身高度') },
    { field: 'weight', name: '產品重量(kg)', required: false, description: t('產品本身重量') },
    { field: 'packagingLength', name: '包裝長度(cm)', required: false, description: t('包裝長度，用於CBM計算') },
    { field: 'packagingWidth', name: '包裝寬度(cm)', required: false, description: t('包裝寬度，用於CBM計算') },
    { field: 'packagingHeight', name: '包裝高度(cm)', required: false, description: t('包裝高度，用於CBM計算') },
    { field: 'packagingWeight', name: '包裝重量(kg)', required: false, description: t('包裝重量') },
    { field: 'temperatureRequirement', name: '溫度要求', required: false, description: t('存儲溫度要求') },
    { field: 'baseUOM', name: '基本單位', required: true, description: t('產品基本計量單位') },
    { field: 'packagingUOM', name: '包裝單位', required: false, description: t('包裝計量單位') },
    { field: 'conversionFactor', name: '轉換因子', required: false, description: t('包裝單位與基本單位的轉換比例（默認為1）') },
    { field: 'currentStock', name: '當前庫存', required: false, description: t('當前庫存數量（默認為0）') },
    { field: 'reorderLevel', name: '重新訂購水平', required: false, description: t('重新訂購閾值（默認為0）') },
    { field: 'safetyStock', name: '安全庫存', required: false, description: t('安全庫存數量（默認為0）') },
    { field: 'maxStock', name: '最大庫存', required: false, description: t('最大庫存數量（默認為0）') },
  ];

  // 可選字段勾選狀態（依賴 fieldDescriptions，需在其定義之後）
  const optionalDefaultKeys = useMemo(() => fieldDescriptions.filter(f => !f.required).map(f => f.field), [fieldDescriptions]);
  const [selectedOptionalKeys, setSelectedOptionalKeys] = useState<string[]>(optionalDefaultKeys);

  // 下載模板（根據勾選生成，必填在前，可選在後）- Excel格式
  const downloadTemplate = () => {
    const required = fieldDescriptions.filter(f => f.required);
    const optional = fieldDescriptions.filter(f => !f.required && selectedOptionalKeys.includes(f.field));
    const selected = [...required, ...optional];

    const example1: Record<string, string> = {
      customer: 'ABC Company', storeCode: 'H6620001', batchControlType: 'FIFO', merchantReference: 'MERCH-001', sku: 'SKU-001', barcode: '1234567890123', productName: '智能筆記本',
      productNameEnglish: 'Smart Notebook', productNameChinese: '智能筆記本', description: t('高性能智能筆記本，配備先進處理器'), brand: 'TechBrand',
      length: '30', width: '20', height: '2', weight: '1.5', packagingLength: '35', packagingWidth: '25', packagingHeight: '5', packagingWeight: '2.0',
      temperatureRequirement: 'room_temperature', baseUOM: 'PCS', packagingUOM: 'BOX', conversionFactor: '1', currentStock: '100', reorderLevel: '20', safetyStock: '10', maxStock: '500'
    };
    const example2: Record<string, string> = {
      customer: 'XYZ Trading Ltd', storeCode: 'B0282001', batchControlType: 'FEFO', merchantReference: 'MERCH-002', sku: 'SKU-002', barcode: '9876543210987', productName: '無線耳機',
      productNameEnglish: 'Wireless Earphones', productNameChinese: '無線耳機', description: t('高音質無線藍牙耳機'), brand: 'AudioPro',
      length: '15', width: '10', height: '3', weight: '0.2', packagingLength: '20', packagingWidth: '15', packagingHeight: '8', packagingWeight: '0.5',
      temperatureRequirement: 'dry', baseUOM: 'PAIR', packagingUOM: 'BOX', conversionFactor: '2', currentStock: '50', reorderLevel: '10', safetyStock: '5', maxStock: '200'
    };
    // 套裝範例：SET-001 由上面兩個單品組成，讓模板下載後可直接往返測試
    const example3: Record<string, string> = {
      customer: 'ABC Company', storeCode: 'H6620001', batchControlType: 'FIFO', merchantReference: 'MERCH-003', sku: 'SET-001', barcode: '4712345678901', productName: '節日禮盒',
      productNameEnglish: 'Holiday Gift Set', productNameChinese: '節日禮盒', description: '智能筆記本 + 無線耳機組合', brand: 'TechBrand',
      length: '40', width: '30', height: '10', weight: '2.0', packagingLength: '45', packagingWidth: '35', packagingHeight: '12', packagingWeight: '2.5',
      temperatureRequirement: 'room_temperature', baseUOM: 'SET', packagingUOM: 'BOX', conversionFactor: '1', currentStock: '0', reorderLevel: '5', safetyStock: '2', maxStock: '100'
    };
    
    // 構建Excel數據
    const headers = selected.map(field => field.name);
    const data = [
      headers,
      selected.map(f => example1[f.field] ?? ''),
      selected.map(f => example2[f.field] ?? ''),
      selected.map(f => example3[f.field] ?? '')
    ];

    // 創建工作簿和工作表
    const worksheet = XLSX.utils.aoa_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, '產品模板');

    // 設置列寬
    const colWidths = selected.map(() => ({ wch: 15 }));
    worksheet['!cols'] = colWidths;

    // 套裝組成：SET-001 由 2x SKU-001 + 3x SKU-002 組成。
    // 這張工作表不受可選欄位勾選影響，一律寫出。
    const bundleSheet = XLSX.utils.aoa_to_sheet([
      ['套裝SKU', '組件SKU', '每套數量'],
      ['SET-001', 'SKU-001', '2'],
      ['SET-001', 'SKU-002', '3'],
    ]);
    bundleSheet['!cols'] = [{ wch: 18 }, { wch: 18 }, { wch: 12 }];
    XLSX.utils.book_append_sheet(workbook, bundleSheet, '套裝組成');

    // 下載Excel文件
    XLSX.writeFile(workbook, '產品批量上傳模板.xlsx');
    message.success(t('Excel模板下載成功'));
  };

  // 文件上傳配置
  const uploadProps: UploadProps = {
    name: 'file',
    accept: '.csv,.xlsx,.xls',
    showUploadList: false,
    beforeUpload: (file) => {
      setUploadedFile(file);
      processFile(file);
      return false;
    },
  };

  // 處理文件
  // 將 Excel 日期序列號轉換為 YYYY-MM-DD 格式
  const convertExcelDate = (value: any): string => {
    if (!value) return '';
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    
    if (typeof value === 'number') {
      const excelEpoch = new Date(1899, 11, 30);
      const date = new Date(excelEpoch.getTime() + value * 86400000);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    
    if (value instanceof Date) {
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, '0');
      const day = String(value.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    
    const str = String(value).trim();
    const patterns = [
      /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/,
      /^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})$/,
    ];
    
    for (const pattern of patterns) {
      const match = str.match(pattern);
      if (match) {
        let year, month, day;
        if (match[1].length === 4) {
          year = match[1];
          month = match[2].padStart(2, '0');
          day = match[3].padStart(2, '0');
        } else {
          day = match[1].padStart(2, '0');
          month = match[2].padStart(2, '0');
          year = match[3];
        }
        return `${year}-${month}-${day}`;
      }
    }
    
    return str;
  };

  const processFile = async (file: UploadFile) => {
    setIsProcessing(true);
    setUploadProgress(0);

    try {
      const fileName = (file as any).name || '';
      const isExcelFile = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');
      
      let parsedData: any[];
      let parsedBundleRows: BundleSheetRow[] = [];

      if (isExcelFile) {
        console.log('檢測到 Excel 文件，使用 XLSX 庫解析');
        const fileContent = await readFileAsArrayBuffer(file);
        setUploadProgress(30);
        const parsed = parseExcelContent(fileContent);
        parsedData = parsed.products;
        parsedBundleRows = parsed.bundleRows;
        setUploadProgress(60);
      } else {
        console.log('檢測到 CSV 文件，使用文本解析');
        const fileContent = await readFileContent(file);
        setUploadProgress(30);
        parsedData = parseCSVContent(fileContent);
        setUploadProgress(60);
      }

      const validatedData = validateParsedData(parsedData);

      // Resolve exactly the SKUs this file references. The wider product list
      // fetched for duplicate checking is capped at 10k rows and the master is
      // far larger, so it cannot be trusted to resolve components.
      let resolved: ResolvedSku[] = [];
      if (parsedBundleRows.length > 0) {
        try {
          const response: any = await apiService.resolveProductSkus(referencedSkusOf(parsedBundleRows));
          resolved = Array.isArray(response?.products) ? response.products : [];
        } catch (e) {
          console.error('解析套裝 SKU 失敗:', e);
          message.warning(t('無法驗證套裝 SKU，將由伺服器端進行最終驗證'));
        }
      }

      const incomingSkus = new Set(
        validatedData.filter(item => item.status !== 'error').map(item => item.sku).filter(Boolean),
      );

      const bundleValidation = validateBundleSheet({
        rows: parsedBundleRows,
        incomingSkus,
        resolved,
      });

      const bundleErrors: ValidationError[] = [];
      for (const [parentSku, error] of bundleValidation.excludedParents) {
        const firstRow = parsedBundleRows.find(r => r.parentSku === parentSku);
        bundleErrors.push({
          row: firstRow?.row ?? 0,
          field: 'parentSku',
          value: parentSku,
          error,
          severity: 'error',
          sheet: 'bundles',
        });
      }
      for (const [parentSku, count] of bundleValidation.overwriteWarnings) {
        const firstRow = parsedBundleRows.find(r => r.parentSku === parentSku);
        bundleErrors.push({
          row: firstRow?.row ?? 0,
          field: 'parentSku',
          value: parentSku,
          error: t('將覆蓋現有套裝組成（現有 {{count}} 項）', { count }),
          severity: 'warning',
          sheet: 'bundles',
        });
      }

      // A parent whose bundle is broken must not be created at all.
      for (const item of validatedData) {
        const excluded = bundleValidation.excludedParents.get(item.sku);
        if (excluded) {
          item.status = 'error';
          item.errors.push({
            row: item.row,
            field: 'sku',
            value: item.sku,
            error: excluded,
            severity: 'error',
            sheet: 'bundles',
          });
        }
        const components = bundleValidation.validBundles.get(item.sku);
        if (components) {
          item.bundleSummary = components
            .map(c => `${c.componentSku} ×${c.quantity}`)
            .join(', ');
        }
      }

      setUploadProgress(90);

      const errors = [...generateValidationErrors(validatedData), ...bundleErrors];

      setValidBundles(bundleValidation.validBundles);
      setPreviewData(validatedData);
      setValidationErrors(errors);
      setUploadProgress(100);
      
      setIsProcessing(false);
      setCurrentStep(1);
    } catch (error) {
      console.error('文件處理錯誤:', error);
      const errorMessage = error instanceof Error ? error.message : t('未知錯誤');
      
      if (errorMessage.includes('格式不正確')) {
        message.error({
          content: t('文件格式錯誤！請確保：1) 文件包含標題行和數據行；2) 如果用Excel編輯過，請另存為CSV (UTF-8) 格式或直接上傳 .xlsx 文件'),
          duration: 6
        });
      } else {
        message.error(t('文件處理失敗：{{errorMessage}}', { errorMessage }));
      }
      
      setIsProcessing(false);
      setUploadProgress(0);
    }
  };

  // 讀取文件內容
  const readFileContent = (file: UploadFile): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        let content = e.target?.result as string;
        
        // 移除 BOM 字符（UTF-8, UTF-16 LE, UTF-16 BE）
        content = content.replace(/^\uFEFF/, ''); // UTF-8 BOM
        content = content.replace(/^\uFFFE/, ''); // UTF-16 LE BOM
        
        console.log('CSV 文件前100個字符:', content.substring(0, 100));
        console.log('CSV 文件第一行:', content.split('\n')[0]);
        
        resolve(content);
      };
      reader.onerror = () => reject(new Error(t('文件讀取失敗')));
      reader.readAsText(file as File, 'UTF-8');
    });
  };

  const readFileAsArrayBuffer = (file: UploadFile): Promise<ArrayBuffer> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as ArrayBuffer;
        resolve(content);
      };
      reader.onerror = () => reject(new Error(t('文件讀取失敗')));
      reader.readAsArrayBuffer(file as unknown as File);
    });
  };

  const parseExcelContent = (arrayBuffer: ArrayBuffer): { products: any[]; bundleRows: BundleSheetRow[] } => {
    try {
      const workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });

      const bundleSheetName = findBundleSheetName(workbook.SheetNames);
      const bundleRows = bundleSheetName
        ? parseBundleSheetRows(
            XLSX.utils.sheet_to_json(workbook.Sheets[bundleSheetName], {
              header: 1,
              defval: '',
              raw: false,
            }) as any[][],
          )
        : [];

      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '', raw: false }) as any[][];

      // A bundle-only file legitimately has no product rows.
      if (jsonData.length < 2 && bundleRows.length === 0) {
        throw new Error('Excel 文件格式不正確，至少需要標題行和一行數據');
      }

      const headers = (jsonData[0] || []).map((h: any) => String(h || '').trim());
      const data: any[] = [];

      for (let i = 1; i < jsonData.length; i++) {
        const row: any = { row: i };
        const values = jsonData[i];

        headers.forEach((header, index) => {
          const fieldKey = getFieldKey(header);
          const value = String(values[index] || '').trim();
          row[fieldKey] = value;
        });

        data.push(row);
      }

      console.log(`成功解析 Excel 文件：${headers.length} 個欄位，${data.length} 行數據，${bundleRows.length} 行套裝組成`);
      return { products: data, bundleRows };
    } catch (error) {
      console.error('Excel 解析錯誤:', error);
      throw new Error(t('Excel 文件解析失敗：') + (error instanceof Error ? error.message : t('未知錯誤')));
    }
  };

  // 解析CSV內容
  const detectDelimiter = (content: string): string => {
    const lines = content.split('\n').filter(line => line.trim());
    if (lines.length === 0) return ',';
    
    const firstLine = lines[0];
    const delimiters = [',', ';', '\t', '|'];
    const counts = delimiters.map(delim => ({
      delimiter: delim,
      count: firstLine.split(delim).length
    }));
    
    counts.sort((a, b) => b.count - a.count);
    return counts[0].count > 1 ? counts[0].delimiter : ',';
  };

  const parseCSVContent = (content: string): any[] => {
    // 移除BOM字符（多種BOM格式）
    content = content.replace(/^\uFEFF/, ''); // UTF-8 BOM
    content = content.replace(/^\uFFFE/, ''); // UTF-16 LE BOM
    content = content.replace(/^\ufeff/, ''); // 小寫版本
    
    const lines = content.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      throw new Error('CSV文件格式不正確，至少需要標題行和一行數據');
    }

    const delimiter = detectDelimiter(content);
    console.log('檢測到的CSV分隔符:', delimiter === ',' ? '逗號' : delimiter === ';' ? '分號' : delimiter === '\t' ? '制表符' : delimiter);

    const headers = parseCSVLine(lines[0], delimiter).map(header => header.trim().replace(/"/g, '').replace(/\r/g, ''));
    
    console.log('解析到的標題行:', headers);
    console.log('標題行映射結果:', headers.map(h => `"${h}" -> "${getFieldKey(h)}"`));
    
    const data: any[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i], delimiter);
      
      // 即使字段數量不匹配也嘗試解析，只是給出警告
      if (values.length !== headers.length) {
        console.warn(`第${i+1}行字段數量不匹配: 期望${headers.length}個字段，實際${values.length}個字段`);
      }
      
      const row: any = { row: i };
      headers.forEach((header, index) => {
        row[getFieldKey(header)] = (index < values.length ? values[index] : '')?.trim() || '';
      });
      
      // 調試：打印第一行數據的映射結果
      if (i === 1) {
        console.log('第一行數據解析結果:', row);
      }
      
      data.push(row);
    }

    console.log(`成功解析 CSV 文件：${headers.length} 個欄位，${data.length} 行數據`);
    return data;
  };

  // 解析CSV行
  const parseCSVLine = (line: string, delimiter: string = ','): string[] => {
    line = line.replace(/\r?\n$/, '');
    
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    let i = 0;
    
    while (i <= line.length) {
      const char = i < line.length ? line[i] : delimiter;
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === delimiter && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else if (i < line.length) {
        current += char;
      }
      
      i++;
    }
    
    return result;
  };

  // 字段映射
  const getFieldKey = (header: string): string => {
    // 清理標題字符串：移除所有不可見字符和多餘空格
    const cleanHeader = header
      .trim()
      .replace(/[\u200B-\u200D\uFEFF]/g, '') // 移除零寬字符
      .replace(/\s+/g, '') // 移除所有空格
      .replace(/[^\u4e00-\u9fa5a-zA-Z0-9()（）]/g, ''); // 只保留中文、英文、數字和括號
    
    const normalized = header.trim();
    // 兼容常見別名與大小寫/空格差異
    const fieldMap: Record<string, string> = {
      '客戶': 'customer',
      '客户': 'customer',
      'CUSTOMER': 'customer',
      'StoreCode': 'storeCode',
      'STORECODE': 'storeCode',
      '店鋪編碼': 'storeCode',
      '店铺编码': 'storeCode',
      'BatchControlType': 'batchControlType',
      'BATCHCONTROLTYPE': 'batchControlType',
      '預設訂單分配策略': 'batchControlType',
      '预设订单分配策略': 'batchControlType',
      '分配策略': 'batchControlType',
      '商家參考': 'merchantReference',
      '商家参考': 'merchantReference',
      'SKUID': 'sku',
      '條形碼': 'barcode',
      '条形码': 'barcode',
      'BARCODE': 'barcode',
      '產品名稱': 'productName',
      '产品名称': 'productName',
      'PRODUCTNAME': 'productName',
      '英文產品名稱': 'productNameEnglish',
      '英文产品名称': 'productNameEnglish',
      '中文產品名稱': 'productNameChinese',
      '中文产品名称': 'productNameChinese',
      '描述': 'description',
      '品牌': 'brand',
      '產品長度(cm)': 'length',
      '產品長度（cm）': 'length',
      '产品长度(cm)': 'length',
      '产品长度（cm）': 'length',
      '產品寬度(cm)': 'width',
      '產品寬度（cm）': 'width',
      '产品宽度(cm)': 'width',
      '产品宽度（cm）': 'width',
      '產品高度(cm)': 'height',
      '產品高度（cm）': 'height',
      '产品高度(cm)': 'height',
      '产品高度（cm）': 'height',
      '產品重量(kg)': 'weight',
      '產品重量（kg）': 'weight',
      '产品重量(kg)': 'weight',
      '产品重量（kg）': 'weight',
      '包裝長度(cm)': 'packagingLength',
      '包裝長度（cm）': 'packagingLength',
      '包装长度(cm)': 'packagingLength',
      '包装长度（cm）': 'packagingLength',
      '包裝寬度(cm)': 'packagingWidth',
      '包裝寬度（cm）': 'packagingWidth',
      '包装宽度(cm)': 'packagingWidth',
      '包装宽度（cm）': 'packagingWidth',
      '包裝高度(cm)': 'packagingHeight',
      '包裝高度（cm）': 'packagingHeight',
      '包装高度(cm)': 'packagingHeight',
      '包装高度（cm）': 'packagingHeight',
      '包裝重量(kg)': 'packagingWeight',
      '包裝重量（kg）': 'packagingWeight',
      '包装重量(kg)': 'packagingWeight',
      '包装重量（kg）': 'packagingWeight',
      '溫度要求': 'temperatureRequirement',
      '温度要求': 'temperatureRequirement',
      '基本單位': 'baseUOM',
      '基本单位': 'baseUOM',
      '包裝單位': 'packagingUOM',
      '包装单位': 'packagingUOM',
      '轉換因子': 'conversionFactor',
      '转换因子': 'conversionFactor',
      '當前庫存': 'currentStock',
      '当前库存': 'currentStock',
      '重新訂購水平': 'reorderLevel',
      '重新订购水平': 'reorderLevel',
      '安全庫存': 'safetyStock',
      '安全库存': 'safetyStock',
      '最大庫存': 'maxStock',
      '最大库存': 'maxStock',
    };
    
    // 首先嘗試使用清理後的標題匹配
    if (fieldMap[cleanHeader]) {
      return fieldMap[cleanHeader];
    }
    
    // 然後嘗試精確匹配，再嘗試小寫匹配
    if (fieldMap[normalized]) return fieldMap[normalized];
    const lower = normalized.toLowerCase();
    const lowerMap: Record<string, string> = {
      'customer': 'customer',
      'store code': 'storeCode',
      'storecode': 'storeCode',
      'batch control type': 'batchControlType',
      'batchcontroltype': 'batchControlType',
      'allocation strategy': 'batchControlType',
      'merchant reference': 'merchantReference',
      'sku': 'sku',
      'sku id': 'sku',
      'barcode': 'barcode',
      'product name': 'productName',
      'base uom': 'baseUOM',
      'packaging uom': 'packagingUOM',
      'conversion factor': 'conversionFactor',
      'current stock': 'currentStock',
      'reorder level': 'reorderLevel',
      'safety stock': 'safetyStock',
      'max stock': 'maxStock',
    };
    return lowerMap[lower] || header;
  };

  // 驗證數據
  const validateParsedData = (data: any[]): PreviewItem[] => {
    return data.map((row, index) => {
      const errors: ValidationError[] = [];
      let status: 'valid' | 'warning' | 'error' = 'valid';

      // 必填字段驗證
      const requiredFields = [
        { key: 'sku', name: 'SKU' },
        { key: 'barcode', name: t('條形碼') },
        { key: 'productName', name: t('產品名稱') },
        { key: 'baseUOM', name: t('基本單位') },
        { key: 'batchControlType', name: t('預設訂單分配策略') }
      ];

      requiredFields.forEach(field => {
        if (!row[field.key] || row[field.key].toString().trim() === '') {
          errors.push({
            row: index + 1,
            field: field.key,
            value: row[field.key],
            error: t('{{fieldName}}不能為空', { fieldName: field.name }),
            severity: 'error'
          });
          status = 'error';
        }
      });

      // Store Code 或客戶至少填寫一個 — same rule as the single-product form in
      // ProductMasterSetup. The backend associates store codes and customers
      // independently and requires neither, so demanding Store Code here
      // rejected rows that are perfectly valid with only a Customer.
      const hasStoreCode = !!row.storeCode && String(row.storeCode).trim() !== '';
      const hasCustomer = !!row.customer && String(row.customer).trim() !== '';
      if (!hasStoreCode && !hasCustomer) {
        errors.push({
          row: index + 1,
          field: 'storeCode',
          value: row.storeCode,
          error: t('Store Code 或客戶至少填寫一個'),
          severity: 'error'
        });
        status = 'error';
      }

      // 數字字段驗證
      const numberFields = ['length', 'width', 'height', 'weight', 'packagingLength', 'packagingWidth', 'packagingHeight', 'packagingWeight', 'conversionFactor', 'currentStock', 'reorderLevel', 'safetyStock', 'maxStock'];
      numberFields.forEach(field => {
        if (row[field] && isNaN(Number(row[field]))) {
          errors.push({
            row: index + 1,
            field: field,
            value: row[field],
            error: t('{{field}}必須為數字', { field }),
            severity: 'error'
          });
          status = 'error';
        }
      });

      // 分配策略值驗證
      if (row.batchControlType && String(row.batchControlType).trim()) {
        const validStrategies = ['fifo', 'fefo', 'lifo', 'lefo', 'random', 'largest_qty_first', 'smallest_qty_first', 'batch_number_asc', 'batch_number_desc'];
        const labelToCode: Record<string, string> = {
          '先進先出': 'fifo', '先进先出': 'fifo',
          '先到期先出': 'fefo', '先到期先出': 'fefo',
          '最大庫存優先': 'largest_qty_first', '最大库存优先': 'largest_qty_first',
        };
        const rawVal = String(row.batchControlType).trim();
        const mapped = labelToCode[rawVal] || rawVal.toLowerCase();
        if (!validStrategies.includes(mapped)) {
          errors.push({
            row: index + 1,
            field: 'batchControlType',
            value: row.batchControlType,
            error: t('分配策略必須為 FIFO、FEFO 或 LARGEST_QTY_FIRST 等有效值'),
            severity: 'error'
          });
          status = 'error';
        } else {
          row.batchControlType = mapped;
        }
      }

      // 重複SKU驗證 - 檢查SKU是否已存在於系統中
      if (row.sku && String(row.sku).trim()) {
        const skuValue = String(row.sku).trim().toLowerCase();
        const existingProductName = existingSkus.get(skuValue);

        if (existingProductName !== undefined) {
          errors.push({
            row: index + 1,
            field: 'sku',
            value: row.sku,
            error: t('SKU "{{sku}}" 已存在於系統中（現有產品：{{productName}}）', { sku: row.sku, productName: existingProductName || t('未知名稱') }),
            severity: 'error'
          });
          status = 'error';
        } else if (existingSkus.size === 0 && !skusLoading) {
          // If SKUs couldn't be loaded, show warning
          errors.push({
            row: index + 1,
            field: 'sku',
            value: row.sku,
            error: t('無法驗證SKU "{{sku}}" 是否重複（產品列表未載入）', { sku: row.sku }),
            severity: 'warning'
          });
          if (status === 'valid') status = 'warning';
        }
      }

      // 客戶驗證 - 如果提供了客戶名稱，必須在系統中存在
      if (row.customer && String(row.customer).trim()) {
        // Normalize customer value: trim, remove extra spaces, convert to lowercase
        const customerValue = String(row.customer).trim().replace(/\s+/g, ' ').toLowerCase();

        // Debug logging
        console.log('Validating customer:', {
          rawValue: row.customer,
          normalizedValue: customerValue,
          availableCustomers: customers.map(c => ({ id: c.id, name: c.name, nameLower: c.name.toLowerCase() }))
        });

        // Check if customer exists - use includes for partial matching as well
        const customerExists = customers.some(c => {
          const nameNormalized = c.name.trim().replace(/\s+/g, ' ').toLowerCase();
          const idLower = c.id.toLowerCase();

          return idLower === customerValue ||
                 nameNormalized === customerValue ||
                 nameNormalized.includes(customerValue) ||
                 customerValue.includes(nameNormalized);
        });

        if (!customerExists && customers.length > 0) {
          errors.push({
            row: index + 1,
            field: 'customer',
            value: row.customer,
            error: t('客戶 "{{customer}}" 不存在於系統中', { customer: row.customer }),
            severity: 'error'
          });
          status = 'error';
        } else if (customers.length === 0 && !customersLoading) {
          // If customers couldn't be loaded, show warning instead of error
          errors.push({
            row: index + 1,
            field: 'customer',
            value: row.customer,
            error: t('無法驗證客戶 "{{customer}}"（客戶列表未載入）', { customer: row.customer }),
            severity: 'warning'
          });
          if (status === 'valid') status = 'warning';
        }
      }

      return {
        row: index + 1,
        merchantReference: row.merchantReference || '',
        sku: row.sku || '',
        productName: row.productName || '',
        productNameEnglish: row.productNameEnglish || undefined,
        productNameChinese: row.productNameChinese || undefined,
        description: row.description || undefined,
        barcode: row.barcode || '',
        brand: row.brand || '',
        length: Number(row.length) || undefined,
        width: Number(row.width) || undefined,
        height: Number(row.height) || undefined,
        weight: Number(row.weight) || undefined,
        packagingLength: Number(row.packagingLength) || undefined,
        packagingWidth: Number(row.packagingWidth) || undefined,
        packagingHeight: Number(row.packagingHeight) || undefined,
        packagingWeight: Number(row.packagingWeight) || undefined,
        temperatureRequirement: row.temperatureRequirement || undefined,
        baseUOM: row.baseUOM || '',
        packagingUOM: row.packagingUOM || undefined,
        conversionFactor: Number(row.conversionFactor) || undefined,
        currentStock: Number(row.currentStock) || 0,
        reorderLevel: Number(row.reorderLevel) || 0,
        safetyStock: Number(row.safetyStock) || 0,
        maxStock: Number(row.maxStock) || 0,
        customer: row.customer || undefined,
        storeCode: row.storeCode || undefined,
        batchControlType: row.batchControlType || undefined,
        status,
        errors
      };
    });
  };

  // 生成驗證錯誤列表
  const generateValidationErrors = (data: PreviewItem[]): ValidationError[] => {
    const errors: ValidationError[] = [];
    data.forEach(item => {
      errors.push(...item.errors);
    });
    return errors;
  };

  // 確認導入
  const confirmImport = () => {
    const validData = previewData.filter(item => item.status === 'valid' || item.status === 'warning');


    const bundleComponents: BundleComponentPayload[] = [];
    for (const [parentSku, components] of validBundles) {
      for (const component of components) {
        bundleComponents.push({
          parentSku,
          componentSku: component.componentSku,
          quantity: component.quantity,
        });
      }
    }

    // A bundle-only file has no product rows but is still a valid import.
    if (validData.length === 0 && bundleComponents.length === 0) {
      message.error(t('沒有有效的數據可以導入'));
      return;
    }

    onUploadComplete(validData, bundleComponents);
    message.success(
      validBundles.size > 0
        ? t('成功導入 {{count}} 個產品，{{bundles}} 個套裝，列表已自動刷新', {
            count: validData.length,
            bundles: validBundles.size,
          })
        : t('成功導入 {{count}} 個產品，列表已自動刷新', { count: validData.length }),
    );
    
    // 直接關閉模態框，不顯示成功頁面
    resetState();
    onCancel();
  };

  // 關閉彈窗
  const handleClose = () => {
    setCurrentStep(0);
    setUploadedFile(null);
    setPreviewData([]);
    setValidationErrors([]);
    setIsProcessing(false);
    setUploadProgress(0);
    onCancel();
  };

  // 預覽表格列定義
  const previewColumns: ColumnsType<PreviewItem> = [
    {
      title: t('行號'),
      dataIndex: 'row',
      width: 60,
      render: (row, record) => (
        <Space>
          <span>{row}</span>
          {record.status === 'error' && <CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
          {record.status === 'warning' && <ExclamationCircleOutlined style={{ color: '#faad14' }} />}
          {record.status === 'valid' && <CheckCircleOutlined style={{ color: '#52c41a' }} />}
        </Space>
      ),
    },
    {
      title: t('產品資訊'),
      key: 'product',
      width: 200,
      render: (_, record) => (
        <div>
          <div><strong>{record.productName || <Text type="danger">{t('缺少產品名稱')}</Text>}</strong></div>
          <Text type="secondary" style={{ fontSize: '12px' }}>{t('SKU:')} {record.sku}</Text><br />
          <Text type="secondary" style={{ fontSize: '12px' }}>{t('商家參考:')} {record.merchantReference}</Text>
          {record.storeCode && <><br /><Text type="secondary" style={{ fontSize: '12px' }}>{t('Store Code:')} {record.storeCode}</Text></>}
          {record.batchControlType && <><br /><Text type="secondary" style={{ fontSize: '12px' }}>{t('分配策略:')} {record.batchControlType.toUpperCase()}</Text></>}
        </div>
      ),
    },
    {
      title: t('套裝組成'),
      dataIndex: 'bundleSummary',
      width: 200,
      render: (value: string) => (value ? <Tag color="purple">{value}</Tag> : <Text type="secondary">-</Text>),
    },
    {
      title: t('條形碼'),
      dataIndex: 'barcode',
      width: 120,
    },
    {
      title: t('尺寸'),
      key: 'dimensions',
      width: 150,
      render: (_, record) => (
        <div>
          {record.packagingLength && record.packagingWidth && record.packagingHeight ? (
            <>
              <div style={{ fontSize: '12px' }}>{t('包裝:')} {record.packagingLength}×{record.packagingWidth}×{record.packagingHeight}cm</div>
              <div style={{ fontSize: '12px', color: '#666' }}>{t('產品:')} {record.length}×{record.width}×{record.height}cm</div>
            </>
          ) : (
            <Text type="secondary">{t('未提供尺寸')}</Text>
          )}
        </div>
      ),
    },
    {
      title: t('庫存'),
      key: 'stock',
      width: 120,
      render: (_, record) => (
        <div>
          <div>{t('當前:')} {record.currentStock}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {t('安全:')} {record.safetyStock} {t('| 最大:')} {record.maxStock}
          </div>
        </div>
      ),
    },
    {
      title: t('狀態'),
      key: 'status',
      width: 100,
      render: (_, record) => {
        let color = 'default';
        let text = t('未知');

        switch (record.status) {
          case 'valid':
            color = 'success';
            text = t('有效');
            break;
          case 'warning':
            color = 'warning';
            text = t('警告');
            break;
          case 'error':
            color = 'error';
            text = t('錯誤');
            break;
        }
        
        return <Tag color={color}>{text}</Tag>;
      },
    },
  ];

  const errorColumns: ColumnsType<ValidationError> = [
    {
      title: t('行號'),
      dataIndex: 'row',
      width: 120,
      // Two sheets share one error list, so a bare row number is ambiguous.
      render: (row: number, record: ValidationError) =>
        record.sheet === 'bundles'
          ? <Text type="secondary">{t('[套裝組成] 第 {{row}} 行', { row })}</Text>
          : row,
    },
    {
      title: t('字段'),
      dataIndex: 'field',
      width: 120,
    },
    {
      title: t('值'),
      dataIndex: 'value',
      width: 150,
      render: (value) => value || <Text type="secondary">{t('空值')}</Text>,
    },
    {
      title: t('錯誤信息'),
      dataIndex: 'error',
      render: (error, record) => (
        <Text type={record.severity === 'error' ? 'danger' : 'warning'}>
          {error}
        </Text>
      ),
    },
    {
      title: t('嚴重性'),
      dataIndex: 'severity',
      width: 80,
      render: (severity) => (
        <Tag color={severity === 'error' ? 'red' : 'orange'}>
          {severity === 'error' ? t('錯誤') : t('警告')}
        </Tag>
      ),
    },
  ];

  const steps = [
    {
      title: t('上傳文件'),
      description: t('選擇並上傳CSV/Excel文件'),
    },
    {
      title: t('數據預覽'),
      description: t('查看和驗證數據'),
    },
    {
      title: t('導入完成'),
      description: t('數據導入成功'),
    },
  ];

  return (
    <Modal
      title={t('批量上傳產品')}
      open={visible}
      onCancel={handleClose}
      width={1200}
      footer={null}
      destroyOnClose
    >
      <Steps current={currentStep} style={{ marginBottom: 24 }}>
        {steps.map(step => (
          <Step key={step.title} title={step.title} description={step.description} />
        ))}
      </Steps>

      {currentStep === 0 && (
        <div>
          <Alert
            message={t('批量上傳說明')}
            description={t('現在支持直接上傳CSV/Excel文件，系統將自動解析和驗證數據。您也可以下載模板文件作為參考。')}
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Card title={t('步驟 1: 下載模板')} style={{ marginBottom: 16 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Paragraph>
                {t('請先下載產品批量上傳模板文件，模板包含所有必需的字段和格式說明。支持Excel格式（.xlsx），可直接用Microsoft Excel或WPS編輯。')}
                                            </Paragraph>
              <div style={{ 
                backgroundColor: '#f6ffed', 
                border: '1px solid #b7eb8f', 
                borderRadius: '6px', 
                padding: '12px', 
                marginBottom: '16px' 
              }}>
                <Text strong style={{ color: '#389e0d' }}>{t('✓ 提示')}</Text>
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', color: '#666' }}>
                  <li>{t('必填字段不能為空，包括SKU、條形碼、產品名稱等')}</li>
                  <li>{t('數量字段請輸入有效的數字')}</li>
                  <li>{t('Excel文件可直接用Microsoft Excel或WPS編輯，無需擔心編碼問題')}</li>
                  <li>{t('包裝尺寸用於CBM計算，產品尺寸記錄產品本身資訊')}</li>
                </ul>
              </div>
              <Button 
                type="primary" 
                icon={<DownloadOutlined />} 
                onClick={downloadTemplate}
                size="large"
              >
                {t('下載Excel模板')}
              </Button>
              <Alert
                type="info"
                showIcon
                style={{ marginTop: 12 }}
                message={t('套裝組成設定僅支援 .xlsx 檔案（CSV 無法儲存第二個工作表）')}
              />
            </Space>
          </Card>

          <Card title={t('步驟 2: 上傳文件')}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Paragraph>
                {t('填寫完成後，請上傳文件進行數據驗證和預覽。')}
                                            </Paragraph>
              {(customersLoading || skusLoading) && (
                <Alert
                  message={
                    customersLoading && skusLoading
                      ? t('正在載入客戶列表和產品列表...')
                      : customersLoading
                      ? t('正在載入客戶列表...')
                      : t('正在載入產品列表（用於重複SKU驗證）...')
                  }
                  type="info"
                  showIcon
                  style={{ marginBottom: 8 }}
                />
              )}
              <Upload {...uploadProps}>
                <Button
                  icon={<UploadOutlined />}
                  size="large"
                  loading={isProcessing || customersLoading || skusLoading}
                  disabled={customersLoading || skusLoading}
                >
                  {customersLoading || skusLoading
                    ? t('載入驗證數據中...')
                    : isProcessing
                    ? t('處理中...')
                    : t('選擇文件上傳')}
                </Button>
              </Upload>
              {uploadedFile && (
                <div>
                  <Text>{t('已選擇文件:')} {uploadedFile.name}</Text>
                  {isProcessing && (
                    <Progress percent={uploadProgress} style={{ marginTop: 8 }} />
                  )}
                </div>
              )}
            </Space>
          </Card>

          <Card title={t('字段說明（必填在前、可選可勾選，用於生成模板）')} style={{ marginTop: 16 }}>
            <Title level={5}>{t('必填字段')}</Title>
            <Row gutter={16} style={{ marginBottom: 12 }}>
              {fieldDescriptions.filter(f => f.required).map((field) => (
                <Col span={8} key={field.field} style={{ marginBottom: 8 }}>
                  <Space>
                    <Tag color="red">{t('必填')}</Tag>
                    <Text strong>{field.name}</Text>
                  </Space>
                  <div style={{ fontSize: '12px', color: '#666', marginLeft: 16 }}>
                    {field.description}
                  </div>
                </Col>
              ))}
            </Row>

            <Title level={5}>{t('可選字段（勾選後包含在模板中）')}</Title>
            <Row gutter={16}>
              {fieldDescriptions.filter(f => !f.required).map((field) => (
                <Col span={8} key={field.field} style={{ marginBottom: 8 }}>
                  <Space align="start">
                    <Checkbox
                      checked={selectedOptionalKeys.includes(field.field)}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? Array.from(new Set([...selectedOptionalKeys, field.field]))
                          : selectedOptionalKeys.filter(k => k !== field.field);
                        setSelectedOptionalKeys(next);
                      }}
                    />
                    <div>
                      <Space>
                        <Tag>{t('可選')}</Tag>
                        <Text strong>{field.name}</Text>
                      </Space>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {field.description}
                      </div>
                    </div>
                  </Space>
                </Col>
              ))}
            </Row>
          </Card>
        </div>
      )}

      {currentStep === 1 && (
        <div>
          <Alert
            message={t('數據驗證完成，共 {{count}} 行數據', { count: previewData.length })}
            description={
              <Space>
                <span>{t('有效:')} {previewData.filter(item => item.status === 'valid').length}</span>
                <span>{t('警告:')} {previewData.filter(item => item.status === 'warning').length}</span>
                <span>{t('錯誤:')} {previewData.filter(item => item.status === 'error').length}</span>
              </Space>
            }
            type={validationErrors.filter(e => e.severity === 'error').length > 0 ? 'warning' : 'success'}
            showIcon
            style={{ marginBottom: 16 }}
          />

          {validationErrors.length > 0 && (
            <Card 
              title={
                <Space>
                  <span>{t('驗證錯誤')}</span>
                  <Tag color="red">
                    {validationErrors.filter(e => e.severity === 'error').length} {t('個錯誤')}
                                                        </Tag>
                  <Tag color="orange">
                    {validationErrors.filter(e => e.severity === 'warning').length} {t('個警告')}
                                                        </Tag>
                </Space>
              } 
              style={{ marginBottom: 16 }}
            >
              {validationErrors.filter(e => e.severity === 'error' && e.sheet !== 'bundles').length > 0 && (
                <Alert
                  message={t('存在錯誤項目')}
                  description={t('請修正所有錯誤項目後才能進行導入。警告項目不會阻止導入，但建議檢查。')}
                  type="error"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}
              {validationErrors.filter(e => e.severity === 'error' && e.sheet === 'bundles').length > 0 && (
                <Alert
                  message={t('部分套裝組成有錯誤')}
                  description={t('有錯誤的套裝SKU不會被建立，其餘產品仍會正常導入。請修正後重新上傳該套裝。')}
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}
              <Table
                dataSource={validationErrors}
                columns={errorColumns}
                rowKey={(record, index) => `${record.row}-${record.field}-${index}`}
                size="small"
                pagination={{ pageSize: 5 }}
              />
            </Card>
          )}

          <Card title={t('數據預覽')} extra={
            <Space>
              <Button onClick={() => setCurrentStep(0)}>{t('重新上傳')}</Button>
              {(() => {
                // A bundle error excludes only its own parent — that parent's row is
                // already marked 'error' and drops out of validData — so unrelated
                // products still import. A product-row error blocks everything, as before.
                const blockingErrorCount = validationErrors.filter(
                  e => e.severity === 'error' && e.sheet !== 'bundles',
                ).length;
                const hasErrors = blockingErrorCount > 0;
                const validDataCount = previewData.filter(item => item.status === 'valid' || item.status === 'warning').length;
                // A bundle-only file has no product rows but is still importable:
                // it redefines compositions over products that already exist.
                const isDisabled = (validDataCount === 0 && validBundles.size === 0) || hasErrors;

                return (
                  <Tooltip
                    title={
                      hasErrors
                        ? t('存在 {{errorCount}} 個錯誤，請修正後重新上傳', { errorCount: blockingErrorCount })
                        : validDataCount === 0 && validBundles.size === 0
                        ? t('沒有有效的數據可以導入')
                        : t('確認導入數據')
                    }
                  >
                    <Button 
                      type={isDisabled ? 'default' : 'primary'}
                      onClick={isDisabled ? undefined : confirmImport}
                      disabled={isDisabled}
                      style={{
                        backgroundColor: isDisabled ? '#f5f5f5' : undefined,
                        borderColor: isDisabled ? '#d9d9d9' : undefined,
                        color: isDisabled ? 'rgba(0, 0, 0, 0.25)' : undefined,
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        opacity: isDisabled ? 0.6 : 1
                      }}
                    >
                      {t('確認導入')}
                                                </Button>
                  </Tooltip>
                );
              })()}
            </Space>
          }>
            <Table
              dataSource={previewData}
              columns={previewColumns}
              rowKey="row"
              size="small"
              pagination={{ pageSize: 10 }}
              rowClassName={(record) => {
                switch (record.status) {
                  case 'error':
                    return 'row-error';
                  case 'warning':
                    return 'row-warning';
                  case 'valid':
                    return 'row-valid';
                  default:
                    return '';
                }
              }}
            />
          </Card>
        </div>
      )}

      {currentStep === 2 && (
        <Result
          status="success"
          title={t('批量上傳完成！')}
          subTitle={t('成功導入 {{count}} 個產品，列表已自動刷新', { count: previewData.filter(item => item.status === 'valid' || item.status === 'warning').length })}
          icon={<CheckCircleOutlined />}
          extra={[
            <Button 
              type="primary" 
              key="close"
              onClick={() => {
                resetState();
                onCancel();
              }}
            >
              {t('關閉並返回列表')}
                              </Button>,
            <Button 
              key="continue"
              onClick={() => {
                resetState();
              }}
            >
              {t('繼續上傳')}
                            </Button>
          ]}
        />
      )}

      <style>{`
        .row-error {
          background-color: #fff2f0 !important;
        }
        .row-warning {
          background-color: #fffbe6 !important;
        }
        .row-valid {
          background-color: #f6ffed !important;
        }
        
        /* 強制禁用按鈕樣式 */
        .ant-btn-primary[disabled], 
        .ant-btn-primary[disabled]:hover, 
        .ant-btn-primary[disabled]:focus, 
        .ant-btn-primary[disabled]:active {
          background-color: #d9d9d9 !important;
          border-color: #d9d9d9 !important;
          color: #666 !important;
          cursor: not-allowed !important;
          opacity: 0.6 !important;
        }
        
        /* 確保禁用狀態下的樣式覆蓋 */
        .ant-btn[disabled] {
          background-color: #f5f5f5 !important;
          border-color: #d9d9d9 !important;
          color: rgba(0, 0, 0, 0.25) !important;
          cursor: not-allowed !important;
          box-shadow: none !important;
        }
      `}</style>
    </Modal>
  );
};

export default ProductBulkUploadModal;
